import * as React from 'react'
import { NavLink, NavLinkProps, useLocation } from 'react-router-dom'
import { ComponentWithAs } from '@reach/utils'
import { TabListProps } from '@reach/tabs'

import { omit } from 'utils/obj'
import { TabList, Tab, Tabs, TabPanel, TabPanels } from 'components/common/Tabs'
import theme from 'theme'

export type SubnavLinkProps = NavLinkProps & {
  enabled?: boolean
  text: React.ReactNode
  showNotification?: boolean
  getIsLinkActive?: (currentPathname: string, to: string) => boolean
}

export const SubnavLink: React.FC<SubnavLinkProps> = ({
  to = '',
  enabled = true,
  text = '',
  showNotification = false,
  ...rest
}) => {
  if (!enabled) return null

  return (
    <Tab
      data-test-subnav-link
      as={NavLink}
      css={[
        showNotification && {
          position: 'relative',
          '&::before': {
            content: "''",
            position: 'absolute',
            top: 0,
            right: -theme.space[1],
            width: theme.space[2],
            height: theme.space[2],
            borderRadius: theme.borderRadius[2],
            background: theme.colors.yellow[0]
          }
        }
      ]}
      to={to}
      {...omit(rest, 'getIsLinkActive')}
    >
      {text}
    </Tab>
  )
}

const Subnav: ComponentWithAs<
  'div',
  TabListProps & {
    getIsLinkActive?: (currentPathname: string, to: string) => boolean
  }
> = ({ children, ...rest }) => {
  const { pathname: currentPathname } = useLocation()
  const linkChildrenTo = React.Children.map(children, child => {
    return child?.props && 'to' in child.props ? child.props : false
  }).filter(Boolean)

  const index = linkChildrenTo.findIndex(props => {
    const {
      getIsLinkActive = (currentPathname, to) => currentPathname.startsWith(to),
      to = ''
    } = props
    return getIsLinkActive(currentPathname, to)
  })
  return (
    <Tabs
      index={index}
      onChange={
        () =>
          undefined /*Hack for reach-tabs hack to work without console errors */
      }
    >
      <TabList
        data-test-subnav
        css={{
          // 1. // These padding offsets should always match `<Content />`
          position: 'sticky',
          top: 'var(--header-height)',
          background: theme.colors.panel[0],
          borderBottom: theme.border.base,
          paddingLeft: theme.space[4], // 1
          paddingRight: theme.space[4], // 1
          paddingBottom: 0,
          paddingTop: theme.space[4],
          whiteSpace: 'nowrap',
          zIndex: theme.zIndices[3], // Less than `<Header />` to not impact dropdown menus
          overscrollBehavior: 'contain', // Don't bleed scroll out from nav

          // Hide scrollbar
          scrollbarWidth: 'none',
          MsOverflowStyle: 'none',
          WebkitOverflowScrolling: 'touch',

          '&::-webkit-scrollbar': {
            width: 0
          },

          [theme.mq[2]]: {
            paddingLeft: theme.space[7], // 1
            paddingRight: theme.space[7] // 1
          }
        }}
        {...rest}
      >
        {children}
      </TabList>
      <TabPanels>
        {/* This is a hack to make a11y checks pass because of the attributes reach/tabs puts on elements*/}
        {Array.from({ length: linkChildrenTo.length }, (_, i) => (
          <TabPanel key={i} />
        ))}
      </TabPanels>
    </Tabs>
  )
}

export default Subnav
